/* 封裝：AES-256-GCM ＋ HKDF 逐筆派生金鑰。
   每一筆記錄用自己的金鑰（由 DEK 派生），所以一筆的外洩不會波及其他筆。 */
import { fromB64, randomBytes, toB64, utf8 } from "./kdf.ts";

export interface Sealed { ivB64: string; ctB64: string }

const IV_BYTES = 12;

export async function aesKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", raw as unknown as ArrayBuffer, "AES-GCM", false, ["encrypt", "decrypt"]);
}

export async function sealWith(key: CryptoKey, plain: Uint8Array, aad?: string): Promise<Sealed> {
  const iv = randomBytes(IV_BYTES);
  const algo: AesGcmParams = { name: "AES-GCM", iv: iv as unknown as ArrayBuffer };
  if (aad) algo.additionalData = utf8(aad) as unknown as ArrayBuffer;
  const ct = await crypto.subtle.encrypt(algo, key, plain as unknown as ArrayBuffer);
  return { ivB64: toB64(iv), ctB64: toB64(new Uint8Array(ct)) };
}

export async function openWith(key: CryptoKey, sealed: Sealed, aad?: string): Promise<Uint8Array> {
  const algo: AesGcmParams = { name: "AES-GCM", iv: fromB64(sealed.ivB64) as unknown as ArrayBuffer };
  if (aad) algo.additionalData = utf8(aad) as unknown as ArrayBuffer;
  const plain = await crypto.subtle.decrypt(algo, key, fromB64(sealed.ctB64) as unknown as ArrayBuffer);
  return new Uint8Array(plain);
}

export async function sealJson(key: CryptoKey, value: unknown, aad?: string): Promise<Sealed> {
  return sealWith(key, utf8(JSON.stringify(value)), aad);
}

export async function openJson<T>(key: CryptoKey, sealed: Sealed, aad?: string): Promise<T> {
  const bytes = await openWith(key, sealed, aad);
  return JSON.parse(new TextDecoder().decode(bytes)) as T;
}

/** 由基底金鑰派生 32 bytes 子金鑰。HKDF 讓同一把 DEK 可以長出無限多把用途不同的金鑰。 */
export async function hkdfSub(base: CryptoKey, info: string, bytes = 32): Promise<Uint8Array> {
  const derived = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0) as unknown as ArrayBuffer, info: utf8(info) as unknown as ArrayBuffer },
    base, bytes * 8);
  return new Uint8Array(derived);
}

export async function hkdfKey(base: CryptoKey, info: string): Promise<CryptoKey> {
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0) as unknown as ArrayBuffer, info: utf8(info) as unknown as ArrayBuffer },
    base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

/** 把原始位元組變成不可匯出的基底金鑰：之後再也拿不回原始位元組。 */
export async function importBaseKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", raw as unknown as ArrayBuffer, "HKDF", false, ["deriveKey", "deriveBits"]);
}

/** 每一筆記錄的金鑰：綁定 entityId，所以記錄被搬到別的位置就解不開。 */
export async function recordKey(base: CryptoKey, entityId: string): Promise<CryptoKey> {
  return hkdfKey(base, "lifesage/record/v1/" + entityId);
}

export async function sha256B64(data: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", utf8(data) as unknown as ArrayBuffer);
  return toB64(new Uint8Array(digest));
}
